import React, { useState, useEffect, useRef } from 'react';
import { Note } from '../../services/notesService';
import './BlockEditor.css';

interface NoteLinkSelectorProps {
  isVisible: boolean;
  notes: Note[];
  searchQuery: string;
  selectedIndex: number;
  onSelect: (note: Note) => void;
  onCreateNew?: (title: string) => void;
  onClose: () => void;
  onSearchChange: (query: string) => void;
  position: { top: number; left: number };
  loading?: boolean;
}

const NoteLinkSelector: React.FC<NoteLinkSelectorProps> = ({
  isVisible,
  notes,
  searchQuery,
  selectedIndex,
  onSelect,
  onCreateNew,
  onClose,
  onSearchChange,
  position,
  loading = false
}) => {
  const searchInputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Filter notes based on search query
  const filteredNotes = React.useMemo(() => {
    if (!searchQuery.trim()) return notes;
    
    const searchTerm = searchQuery.toLowerCase();
    return notes.filter(note => 
      note.title.toLowerCase().includes(searchTerm) ||
      note.note_id.toLowerCase().includes(searchTerm)
    ).sort((a, b) => {
      // Prioritize exact matches and title matches
      const aTitle = a.title.toLowerCase();
      const bTitle = b.title.toLowerCase();
      
      if (aTitle.startsWith(searchTerm) && !bTitle.startsWith(searchTerm)) return -1;
      if (!aTitle.startsWith(searchTerm) && bTitle.startsWith(searchTerm)) return 1;
      
      return a.title.localeCompare(b.title);
    });
  }, [notes, searchQuery]);

  // Check if we should show "Create new note" option
  const shouldShowCreateOption = React.useMemo(() => {
    if (!searchQuery.trim() || !onCreateNew) return false;
    
    // Check if there's an exact title match
    const exactMatch = filteredNotes.some(note => 
      note.title.toLowerCase() === searchQuery.toLowerCase()
    );
    
    return !exactMatch;
  }, [filteredNotes, searchQuery, onCreateNew]);

  // Total items including create option
  const totalItems = filteredNotes.length + (shouldShowCreateOption ? 1 : 0);

  // Auto-focus search input when visible
  useEffect(() => {
    if (isVisible && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isVisible]);

  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onSearchChange(e.target.value);
  };

  // Handle note selection
  const handleNoteSelect = (note: Note) => {
    onSelect(note);
  };

  // Handle create new note
  const handleCreateNew = () => {
    if (onCreateNew && searchQuery.trim()) {
      onCreateNew(searchQuery.trim());
    }
  };

  // Highlight matching text in note titles
  const highlightMatch = (text: string, query: string) => {
    if (!query.trim()) return text;
    
    const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);
    
    return parts.map((part, index) => 
      regex.test(part) ? (
        <span key={index} className="note-link-highlight">{part}</span>
      ) : part
    );
  };

  if (!isVisible) return null;

  return (
    <div 
      ref={containerRef}
      className="note-link-selector"
      style={{ top: position.top, left: position.left }}
    >
      <div className="note-link-selector-content">
        <div className="note-link-search-container">
          <input
            ref={searchInputRef}
            type="text"
            value={searchQuery}
            onChange={handleSearchChange}
            placeholder="Search notes..."
            className="note-link-search-input"
          />
        </div>
        
        {loading ? (
          <div className="note-link-selector-loading">Loading notes...</div>
        ) : (
          <div className="note-link-list">
            {totalItems === 0 ? (
              <div className="note-link-selector-empty">
                {searchQuery.trim() ? 'No notes found' : 'No notes available'}
              </div>
            ) : (
              <>
                {filteredNotes.map((note, index) => (
                  <div
                    key={note.note_id}
                    className={`note-link-item ${index === selectedIndex ? 'selected' : ''}`}
                    onClick={() => handleNoteSelect(note)}
                    role="option"
                    aria-selected={index === selectedIndex}
                  >
                    <span className="note-link-icon">📝</span>
                    <div className="note-link-text">
                      <span className="note-link-title">
                        {highlightMatch(note.title, searchQuery)}
                      </span>
                      <span className="note-link-id">/{note.note_id}</span>
                    </div>
                  </div>
                ))}
                {shouldShowCreateOption && (
                  <div
                    className={`note-link-item note-link-create ${filteredNotes.length === selectedIndex ? 'selected' : ''}`}
                    onClick={handleCreateNew}
                    role="option"
                    aria-selected={filteredNotes.length === selectedIndex}
                  >
                    <span className="note-link-icon">✨</span>
                    <div className="note-link-text">
                      <span className="note-link-title">
                        Create new note: "{searchQuery}"
                      </span>
                      <span className="note-link-id">Press Enter to create</span>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}
        
        <div className="note-link-selector-footer">
          <span className="note-link-hint">
            ↑↓ to navigate • Enter to select • Esc to cancel
          </span>
        </div>
      </div>
    </div>
  );
};

export default NoteLinkSelector;
